import { Contract, parseUnits, formatUnits } from 'ethers';
import { JOB_CONTRACT_ADDRESS, USDC_TOKEN_ABI, USDC_TOKEN_ADDRESS } from './Constants';

// Gas limits for different operations
const GAS_LIMITS = {
  SET_FEE: 100000n,
  ADD_FUNDS: 100000n,
  WITHDRAW: 150000n,
  CHECK_DEPLOYER: 50000n,
};

const resolveRunner = async (runner) => {
  if (!runner) {
    throw new Error('A signer or provider is required');
  }
  if (typeof runner.then === 'function') {
    runner = await runner;
  }
  return runner;
};

const ensureSigner = async (runner) => {
  const resolved = await resolveRunner(runner);
  if (typeof resolved.getAddress !== 'function') {
    throw new Error('A signer with provider access is required');
  }
  return resolved;
};

const getDeployerContract = async (signerOrProvider) => {
  const runner = await resolveRunner(signerOrProvider);
  const DEPLOYER_ABI = [
    'function owner() view returns (address)',
    'function jobFeeBasisPoints() view returns (uint256)',
    'function setJobFee(uint256)',
    'function getContractBalance() view returns (uint256)',
    'function withdraw(address,uint256)',
    'function fundEscrow(uint256)',
    'function checkDeployer() view returns (bool)'
  ];
  return new Contract(JOB_CONTRACT_ADDRESS, DEPLOYER_ABI, runner);
};

export const checkDeployer = async (signer) => {
  const resolvedSigner = await ensureSigner(signer);
  const contract = await getDeployerContract(resolvedSigner);
  const from = await resolvedSigner.getAddress();
  return contract.checkDeployer({ from });
};

export const getContractOwner = async (providerOrSigner) => {
  const contract = await getDeployerContract(providerOrSigner);
  return contract.owner();
};

export const getJobFeeBasisPoints = async (providerOrSigner) => {
  const contract = await getDeployerContract(providerOrSigner);
  const fee = await contract.jobFeeBasisPoints();
  return Number(fee);
};

export const setJobFeeBasisPoints = async ({ signer, feeBasisPoints }) => {
  if (feeBasisPoints < 0) {
    throw new Error('Fee must be positive');
  }
  const resolvedSigner = await ensureSigner(signer);
  const contract = await getDeployerContract(resolvedSigner);
  
  // Estimate gas first, then use explicit limit if estimation fails
  let gasLimit = GAS_LIMITS.SET_FEE;
  try {
    const estimatedGas = await contract.setJobFee.estimateGas(feeBasisPoints);
    gasLimit = estimatedGas + (estimatedGas / 10n); // Add 10% buffer
  } catch (error) {
    console.warn('Gas estimation failed, using default limit:', error.message);
  }
  
  const tx = await contract.setJobFee(feeBasisPoints, { gasLimit });
  await tx.wait();
  return tx;
};

export const getContractBalance = async (signer) => {
  const resolvedSigner = await ensureSigner(signer);
  const contract = await getDeployerContract(resolvedSigner);
  const from = await resolvedSigner.getAddress();
  try {
    const balance = await contract.getContractBalance({ from });
    return balance;
  } catch (error) {
    // If onlyOwner check fails, read payment token balance directly.
    if (error.message?.includes('Only owner') || error.code === 'CALL_EXCEPTION') {
      const provider = resolvedSigner.provider;
      if (provider) {
        const token = new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, provider);
        const balance = await token.balanceOf(JOB_CONTRACT_ADDRESS);
        return balance;
      }
    }
    throw error;
  }
};

export const withdrawFromContract = async ({ signer, to, amountEth, amountUsdc }) => {
  if (!to) {
    throw new Error('Destination address is required');
  }
  const amountValue = amountUsdc ?? amountEth;
  if (!amountValue || Number(amountValue) <= 0) {
    throw new Error('Withdrawal amount must be greater than zero');
  }
  const resolvedSigner = await ensureSigner(signer);
  const contract = await getDeployerContract(resolvedSigner);
  const token = new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, resolvedSigner);
  const decimals = Number(await token.decimals());
  const amountWei = parseUnits(amountValue.toString(), decimals);
  
  // Verify contract balance before attempting withdrawal
  let contractBalance;
  try {
    const from = await resolvedSigner.getAddress();
    contractBalance = await contract.getContractBalance({ from });
  } catch (error) {
    throw error;
  }
  
  if (contractBalance < amountWei) {
    const balanceEth = formatUnits(contractBalance, decimals);
    throw new Error(
      `Insufficient contract balance. Available: ${balanceEth} USDC, Requested: ${amountValue} USDC`
    );
  }
  
  // Estimate gas first, then use explicit limit if estimation fails
  let gasLimit = GAS_LIMITS.WITHDRAW;
  try {
    const estimatedGas = await contract.withdraw.estimateGas(to, amountWei);
    gasLimit = estimatedGas + (estimatedGas / 10n); // Add 10% buffer
  } catch (error) {
    console.warn('Gas estimation failed, using default limit:', error.message);
  }
  
  const tx = await contract.withdraw(to, amountWei, { gasLimit });
  await tx.wait();
  return tx;
};

export const addFundsToContract = async ({ signer, amountEth, amountUsdc }) => {
  const amountValue = amountUsdc ?? amountEth;
  if (!amountValue || Number(amountValue) <= 0) {
    throw new Error('Funding amount must be greater than zero');
  }
  const resolvedSigner = await ensureSigner(signer);
  const contract = await getDeployerContract(resolvedSigner);
  const token = new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, resolvedSigner);
  const decimals = Number(await token.decimals());
  const amountWei = parseUnits(amountValue.toString(), decimals);

  const allowance = await token.allowance(await resolvedSigner.getAddress(), JOB_CONTRACT_ADDRESS);
  if (allowance < amountWei) {
    const approveTx = await token.approve(JOB_CONTRACT_ADDRESS, amountWei);
    await approveTx.wait();
  }

  let gasLimit = GAS_LIMITS.ADD_FUNDS;
  try {
    const estimatedGas = await contract.fundEscrow.estimateGas(amountWei);
    gasLimit = estimatedGas + (estimatedGas / 10n);
  } catch (error) {
    console.warn('Gas estimation failed, using default limit:', error.message);
  }

  const tx = await contract.fundEscrow(amountWei, { gasLimit });
  await tx.wait();
  return tx;
};

